<template>
  <div class="config-panel" :class="layoutClass">
    <b-card title="Конфигурация анализа">
      <b-alert variant="info" show>
        <strong>Совет:</strong> Базовая конфигурация загружается автоматически из сохраненного состояния.
        Изменения применяются при следующем анализе.
      </b-alert>

      <b-alert v-if="currentDistance" variant="primary" show class="mb-3">
        <b-icon icon="info-circle" class="mr-2" />
        <strong>Конфигурация для дистанции:</strong> {{ currentDistance.name }}
        <br>
        <small class="text-muted">Каждая дистанция имеет свои чекпоинты и настройки анализа</small>
      </b-alert>

      <!-- Basic settings -->
      <h6>Основные настройки</h6>
      <b-row>
        <b-col md="4">
          <b-form-group>
            <template #label>
              Время старта
              <b-icon
                v-b-tooltip.hover.right="'Время начала забега в выбранном ниже часовом поясе. В API сохраняется как UTC. Если не указано, берется из конфигурации участников.'"
                icon="info-circle"
                class="ml-1 text-primary"
                font-scale="0.9"
              />
            </template>
            <b-form-input
              :value="startTimeForInput"
              type="datetime-local"
              @input="onStartTimeInput"
            />
          </b-form-group>
        </b-col>

        <b-col md="4">
          <b-form-group>
            <template #label>
              Часовой пояс
              <b-icon
                v-b-tooltip.hover.right="'Часовой пояс для отображения локального времени во всех результатах и считываниях. По умолчанию: Новосибирск (GMT+7).'"
                icon="info-circle"
                class="ml-1 text-primary"
                font-scale="0.9"
              />
            </template>
            <b-form-select
              :value="localConfig.timezone || defaultTimezone"
              :options="timezoneOptions"
              @change="updateField('timezone', $event)"
            />
          </b-form-group>
        </b-col>

        <b-col md="4">
          <b-form-group>
            <template #label>
              Интервал повторов (сек)
              <b-icon
                v-b-tooltip.hover.right="'Минимальный интервал между считываниями для определения дубликатов. Считывания с одного чипа ближе чем этот интервал считаются повторами.'"
                icon="info-circle"
                class="ml-1 text-primary"
                font-scale="0.9"
              />
            </template>
            <b-form-input
              :value="getAnalysisParam('repeatDetectionInterval', 5)"
              type="number"
              min="0"
              @input="updateRepeatDetectionInterval"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group>
            <template #label>
              Приоритет выбора
              <b-icon
                v-b-tooltip.hover.right="'Правило выбора при нескольких считываниях на одном чекпоинте: минимальное время, максимальное или среднее.'"
                icon="info-circle"
                class="ml-1 text-primary"
                font-scale="0.9"
              />
            </template>
            <b-form-select
              :value="getAnalysisParam('priorityRule', 'min')"
              :options="priorityOptions"
              @change="updateAnalysisParam('priorityRule', $event)"
            />
          </b-form-group>
        </b-col>

      </b-row>

      <!-- Chip time toggle: adds a second result column (Chip) and marks first CP as chip start -->
      <b-row>
        <b-col md="12">
          <b-form-group>
            <b-form-checkbox
              :checked="isChipTimeEnabled"
              @change="updateChipTimeEnabled($event)"
            >
              <span>
                Добавить Chip Time
                <b-icon
                  v-b-tooltip.hover.right="'Если выключено — один столбец результата по Gun Time (от официального старта гонки). Если включено — первая точка конфигурации помечается как Chip start, последняя — как Chip finish, и в таблицу результатов добавляется второй столбец с Chip Time (время от первой до последней точки).'"
                  icon="info-circle"
                  class="ml-1 text-primary"
                  font-scale="0.9"
                />
              </span>
            </b-form-checkbox>
            <small class="text-muted">
              Без галочки в результатах один столбец — Gun Time от официального старта гонки.
              С галочкой первая точка конфигурации становится Chip start, последняя — Chip finish,
              и в таблицу добавляется второй столбец Chip Time (нужно минимум две контрольные точки).
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="isChipTimeEnabled">
        <b-col md="4">
          <b-form-group>
            <template #label>
              Допуск раньше старта, 1-я точка (сек)
              <b-icon
                v-b-tooltip.hover.right="'Считывание на первой точке (chip start) может прийти на несколько секунд раньше официального времени старта — это окно в секундах. По умолчанию 2.'"
                icon="info-circle"
                class="ml-1 text-primary"
                font-scale="0.9"
              />
            </template>
            <b-form-input
              :value="chipStartEarlyToleranceSecForInput"
              type="number"
              min="0"
              max="120"
              step="1"
              @input="onChipStartToleranceInput"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="isChipTimeEnabled">
        <b-col md="6">
          <b-form-group>
            <b-form-checkbox
              :checked="!!localConfig.chipGunFallbackEnabled"
              @change="updateChipGunFallback($event)"
            >
              <span>
                Подменять старт по чипу на старт по выстрелу, если отметки нет
                <b-icon
                  v-b-tooltip.hover.right="'Если на старте нет отметки чипа, для расчёта chip-time использовать официальное время старта (gun). Применение помечается в результатах как fallback.'"
                  icon="info-circle"
                  class="ml-1 text-primary"
                  font-scale="0.9"
                />
              </span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <hr>
      <h6>Публичные результаты</h6>
      <b-row>
        <b-col md="6">
          <b-form-group>
            <template #label>
              Формат времени на публичной странице
              <b-icon
                v-b-tooltip.hover.right="'Применяется только к публичной странице результатов и не влияет на админ-выдачу.'"
                icon="info-circle"
                class="ml-1 text-primary"
                font-scale="0.9"
              />
            </template>
            <b-form-select
              :value="publicTimeFormatForInput"
              :options="publicTimeFormatOptions"
              @change="updatePublicTimeFormat($event)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group>
            <b-form-checkbox
              :checked="!!localConfig.publicShowChipTime"
              @change="updatePublicShowChipTime($event)"
            >
              <span>
                Показывать chip-time на публичной странице
                <b-icon
                  v-b-tooltip.hover.right="'Включает дополнительный столбец «Чип-тайм» рядом с результатом по выстрелу.'"
                  icon="info-circle"
                  class="ml-1 text-primary"
                  font-scale="0.9"
                />
              </span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Summary -->
      <hr>
      <h6>Текущая конфигурация</h6>
      <b-row>
        <b-col md="4">
          <div class="config-stat">
            <div class="stat-value">{{ checkpointsCount }}</div>
            <div class="stat-label">Чекпоинтов</div>
          </div>
        </b-col>
        <b-col md="4">
          <div class="config-stat">
            <div class="stat-value">{{ participantsCount }}</div>
            <div class="stat-label">Участников</div>
          </div>
        </b-col>
        <b-col md="4">
          <div class="config-stat">
            <div class="stat-value">{{ virtualReadingsCount }}</div>
            <div class="stat-label">Виртуальных считываний</div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Checkpoints management -->
    <checkpoints-list
      :checkpoints="localConfig.track.checkpoints"
      :official-time-mode="localConfig.officialTimeMode || 'gunTime'"
      @update="updateCheckpoints"
    />

    <!-- Participants management -->
    <participants-list
      :participants="localConfig.userConfigs"
      :available-distances="availableDistances"
      :timezone="localConfig.timezone || defaultTimezone"
      :invalid-participants-filter-request="invalidParticipantsFilterRequest"
      @update="updateParticipants"
      @validation-changed="emitValidationChanged"
    />

    <!-- JSON preview -->
    <b-card title="Конфигурация (JSON)" class="mt-3">
      <details>
        <summary class="cursor-pointer">Показать JSON конфигурации</summary>
        <pre class="config-json mt-2">{{ JSON.stringify(localConfig, null, 2) }}</pre>
      </details>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BAlert, BBadge, BIcon, VBTooltip } from 'bootstrap-vue'
import CheckpointsList from './CheckpointsList.vue'
import ParticipantsList from './ParticipantsList.vue'
import {
  TIMEZONES,
  DEFAULT_TIMEZONE,
  startTimeIsoToInputValue,
  inputValueToStartTimeIso,
} from '../../utils/timezone'

export default {
  name: 'ConfigPanel',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BAlert,
    BBadge,
    BIcon,
    CheckpointsList,
    ParticipantsList,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    availableDistances: {
      type: Array,
      default: () => [],
    },
    currentDistance: {
      type: Object,
      default: null,
    },
    invalidParticipantsFilterRequest: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      localConfig: null,
      defaultTimezone: DEFAULT_TIMEZONE,
      timezoneOptions: TIMEZONES.map(tz => ({
        value: tz.value,
        text: tz.label,
      })),
      priorityOptions: [
        { value: 'min', text: 'Минимальное время' },
        { value: 'max', text: 'Максимальное время' },
        { value: 'average', text: 'Среднее время' },
      ],
      layoutClass: 'light-layout',
      themeObserver: null,
    }
  },
  computed: {
    checkpointsCount() {
      return this.localConfig && this.localConfig.track && this.localConfig.track.checkpoints
        ? this.localConfig.track.checkpoints.length
        : 0
    },

    participantsCount() {
      return this.localConfig && this.localConfig.userConfigs
        ? this.localConfig.userConfigs.length
        : 0
    },

    virtualReadingsCount() {
      return this.localConfig && this.localConfig.virtualReadings
        ? this.localConfig.virtualReadings.length
        : 0
    },

    /** Wall time in selected timezone for datetime-local (not browser local). */
    startTimeForInput() {
      if (!this.localConfig) return ''
      const tz = this.localConfig.timezone || DEFAULT_TIMEZONE
      return startTimeIsoToInputValue(this.localConfig.startTime, tz)
    },

    /** Chip time column is active when officialTimeMode === 'chipTime'. */
    isChipTimeEnabled() {
      return !!this.localConfig && this.localConfig.officialTimeMode === 'chipTime'
    },

    chipStartEarlyToleranceSecForInput() {
      if (!this.localConfig) return 2
      const v = this.localConfig.chipStartEarlyToleranceSec
      if (v === undefined || v === null || v === '') return 2
      const n = Number(v)
      return Number.isFinite(n) ? n : 2
    },

    publicTimeFormatForInput() {
      const v = this.localConfig && this.localConfig.publicTimeFormat
      return v === 'hmsHundredths' ? 'hmsHundredths' : 'hms'
    },

    publicTimeFormatOptions() {
      return [
        { value: 'hms', text: 'Часы:минуты:секунды (0:00:00)' },
        { value: 'hmsHundredths', text: 'Часы:минуты:секунды.сотые (0:00:00.00)' },
      ]
    },
  },
  watch: {
    config: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.localConfig = JSON.parse(JSON.stringify(newVal || {}))
      },
    },
  },
  mounted() {
    this.setupThemeObserver()
  },
  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
  },
  methods: {
    detectTheme() {
      const html = document.documentElement
      const body = document.body
      
      if (html.classList.contains('dark-layout') || 
          body.classList.contains('dark-layout') ||
          html.classList.contains('theme-dark')) {
        this.layoutClass = 'dark-layout'
      } else {
        this.layoutClass = 'light-layout'
      }
    },

    setupThemeObserver() {
      this.detectTheme()
      
      const observerConfig = { attributes: true, attributeFilter: ['class'] }
      this.themeObserver = new MutationObserver(() => {
        this.detectTheme()
      })
      
      this.themeObserver.observe(document.documentElement, observerConfig)
      this.themeObserver.observe(document.body, observerConfig)
    },

    updateField(field, value) {
      this.localConfig[field] = value
      this.emitUpdate()
    },

    onStartTimeInput(value) {
      const tz = (this.localConfig && this.localConfig.timezone) || DEFAULT_TIMEZONE
      this.localConfig.startTime = inputValueToStartTimeIso(value, tz)
      this.emitUpdate()
    },

    getAnalysisParam(param, defaultValue) {
      if (this.localConfig && this.localConfig.analysisParams && this.localConfig.analysisParams[param] !== undefined) {
        return this.localConfig.analysisParams[param]
      }
      return defaultValue
    },

    updateAnalysisParam(param, value) {
      if (!this.localConfig.analysisParams) {
        this.$set(this.localConfig, 'analysisParams', {})
      }
      this.$set(this.localConfig.analysisParams, param, value)
      this.emitUpdate()
    },

    updateRepeatDetectionInterval(value) {
      // b-form-input emits a string; the API validates a number
      const n = value === '' || value === null ? undefined : Number(value)
      this.updateAnalysisParam('repeatDetectionInterval', Number.isFinite(n) ? n : undefined)
    },

    updateChipTimeEnabled(enabled) {
      this.$set(this.localConfig, 'officialTimeMode', enabled ? 'chipTime' : 'gunTime')
      if (enabled) {
        const cur = this.localConfig.chipStartEarlyToleranceSec
        if (cur === undefined || cur === null || cur === '') {
          this.$set(this.localConfig, 'chipStartEarlyToleranceSec', 2)
        }
      }
      this.emitUpdate()
    },

    onChipStartToleranceInput(val) {
      const n = parseInt(String(val), 10)
      if (Number.isNaN(n) || n < 0) {
        this.$set(this.localConfig, 'chipStartEarlyToleranceSec', 0)
      } else {
        this.$set(this.localConfig, 'chipStartEarlyToleranceSec', Math.min(120, n))
      }
      this.emitUpdate()
    },

    updateChipGunFallback(enabled) {
      this.$set(this.localConfig, 'chipGunFallbackEnabled', !!enabled)
      this.emitUpdate()
    },

    updatePublicTimeFormat(value) {
      const next = value === 'hmsHundredths' ? 'hmsHundredths' : 'hms'
      this.$set(this.localConfig, 'publicTimeFormat', next)
      this.emitUpdate()
    },

    updatePublicShowChipTime(enabled) {
      this.$set(this.localConfig, 'publicShowChipTime', !!enabled)
      this.emitUpdate()
    },

    updateCheckpoints(checkpoints) {
      if (!this.localConfig.track) {
        this.$set(this.localConfig, 'track', {})
      }
      this.$set(this.localConfig.track, 'checkpoints', checkpoints)
      this.emitUpdate()
    },

    updateParticipants(participants) {
      this.$set(this.localConfig, 'userConfigs', participants)
      this.emitUpdate()
    },

    emitUpdate() {
      this.$emit('update:config', this.localConfig)
    },

    emitValidationChanged(data) {
      this.$emit('validation-changed', data)
    },
  },
}
</script>

<style scoped>
/* Dark theme for config stats */
.config-panel.dark-layout .config-stat {
  background: linear-gradient(118deg, #252b42, #1e2439);
  border: 1px solid #3b4253;
}

.config-panel.dark-layout .config-stat:hover {
  border-color: #9d93f6;
  box-shadow: 0 2px 8px 0 rgba(157, 147, 246, 0.2);
}

.config-panel.dark-layout .stat-value {
  color: #9d93f6;
}

.config-panel.dark-layout .stat-label {
  color: #a8aaae;
}

.config-panel.dark-layout .config-json {
  background: #1a1e34;
  border: 1px solid #3b4253;
  color: #d0d2d6;
}

.config-panel.dark-layout summary {
  color: #9d93f6;
}

.config-panel.dark-layout summary:hover {
  color: #b4a9ff;
}

/* Light theme for config stats */
.config-panel.light-layout .config-stat {
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
}

.config-panel.light-layout .config-stat:hover {
  border-color: #7367f0;
  box-shadow: 0 2px 8px 0 rgba(115, 103, 240, 0.1);
}

.config-panel.light-layout .stat-value {
  color: #7367f0;
}

.config-panel.light-layout .stat-label {
  color: #b9b9c3;
}

.config-panel.light-layout .config-json {
  background: #fafafa;
  border: 1px solid #ebe9f1;
  color: #5e5873;
}

.config-panel.light-layout summary {
  color: #7367f0;
}

.config-panel.light-layout summary:hover {
  color: #5e50ee;
}

/* Common styles */
.config-stat {
  text-align: center;
  padding: 1.143rem;
  border-radius: 0.428rem;
  transition: all 0.2s;
}

.stat-value {
  font-size: 1.714rem;
  font-weight: 600;
  margin-bottom: 0.286rem;
}

.stat-label {
  font-size: 0.857rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.config-json {
  padding: 1.143rem;
  border-radius: 0.428rem;
  font-size: 0.857rem;
  max-height: 400px;
  overflow: auto;
  font-family: 'Courier New', monospace;
}

details {
  cursor: pointer;
}

summary {
  font-weight: 500;
  padding: 0.5rem 0;
  user-select: none;
}

.cursor-pointer {
  cursor: pointer;
}
</style>

